import { useMemo } from 'react'

/* Minimale, XSS-veilige markdown-renderer. Geen dangerouslySetInnerHTML.
   Ondersteunt: # / ## / ### headings, **bold**, *italic*, `code`,
   [link](url), blockquotes (>), ongesorteerde lijsten (-) en
   fenced code-blocks (``` … ```). Alles wat niet matcht wordt als
   paragraaf gerenderd, dus onbekende syntax valt terug op platte tekst. */

function renderInline(text, keyPrefix = 'inl') {
  const parts = []
  let i = 0
  let idx = 0
  const pushText = (s) => { if (s) parts.push(s) }

  // Regex-loop met named alternates. Volgorde: code > bold > italic > link.
  const re = /(`([^`]+)`)|(\*\*([^*]+)\*\*)|(\*([^*]+)\*)|(\[([^\]]+)\]\(([^)]+)\))/g
  let m
  let lastIndex = 0
  while ((m = re.exec(text)) !== null) {
    pushText(text.slice(lastIndex, m.index))
    if (m[1]) {
      parts.push(<code key={`${keyPrefix}-${idx++}`} className="px-1 py-0.5 rounded bg-muted font-mono text-xs">{m[2]}</code>)
    } else if (m[3]) {
      parts.push(<strong key={`${keyPrefix}-${idx++}`}>{m[4]}</strong>)
    } else if (m[5]) {
      parts.push(<em key={`${keyPrefix}-${idx++}`}>{m[6]}</em>)
    } else if (m[7]) {
      parts.push(<a key={`${keyPrefix}-${idx++}`} href={m[9]} target="_blank" rel="noopener noreferrer" className="text-acid hover:underline">{m[8]}</a>)
    }
    lastIndex = re.lastIndex
  }
  pushText(text.slice(lastIndex))
  return parts.length === 1 && typeof parts[0] === 'string' ? parts[0] : parts
}

export default function MarkdownView({ source }) {
  const blocks = useMemo(() => parseBlocks(source || ''), [source])
  return (
    <div className="prose-wiki max-w-none">
      {blocks.map((b, i) => renderBlock(b, i))}
    </div>
  )
}

function parseBlocks(text) {
  const lines = text.replace(/\r\n/g, '\n').split('\n')
  const blocks = []
  let i = 0
  while (i < lines.length) {
    const line = lines[i]

    // fenced code
    if (line.startsWith('```')) {
      const body = []
      i++
      while (i < lines.length && !lines[i].startsWith('```')) {
        body.push(lines[i])
        i++
      }
      i++
      blocks.push({ type: 'code', body: body.join('\n') })
      continue
    }

    if (/^\s*$/.test(line)) { i++; continue }

    if (line.startsWith('### ')) { blocks.push({ type: 'h3', text: line.slice(4) }); i++; continue }
    if (line.startsWith('## '))  { blocks.push({ type: 'h2', text: line.slice(3) }); i++; continue }
    if (line.startsWith('# '))   { blocks.push({ type: 'h1', text: line.slice(2) }); i++; continue }

    if (line.startsWith('> ')) {
      const body = [line.slice(2)]
      i++
      while (i < lines.length && lines[i].startsWith('> ')) {
        body.push(lines[i].slice(2)); i++
      }
      blocks.push({ type: 'quote', text: body.join(' ') })
      continue
    }

    if (/^\s*[-*]\s/.test(line)) {
      const items = []
      while (i < lines.length && /^\s*[-*]\s/.test(lines[i])) {
        items.push(lines[i].replace(/^\s*[-*]\s/, ''))
        i++
      }
      blocks.push({ type: 'ul', items })
      continue
    }

    if (/^\s*\d+\.\s/.test(line)) {
      const items = []
      while (i < lines.length && /^\s*\d+\.\s/.test(lines[i])) {
        items.push(lines[i].replace(/^\s*\d+\.\s/, ''))
        i++
      }
      blocks.push({ type: 'ol', items })
      continue
    }

    // paragraaf: pak alles tot lege regel of nieuw block-teken
    const body = [line]
    i++
    while (i < lines.length && lines[i].trim() && !/^(#{1,3}\s|>\s|\s*[-*]\s|\s*\d+\.\s|```)/.test(lines[i])) {
      body.push(lines[i])
      i++
    }
    blocks.push({ type: 'p', text: body.join(' ') })
  }
  return blocks
}

function renderBlock(b, key) {
  switch (b.type) {
    case 'h1': return <h1 key={key} className="text-2xl font-bold mt-6 mb-3">{renderInline(b.text, `h1-${key}`)}</h1>
    case 'h2': return <h2 key={key} className="text-xl font-semibold mt-6 mb-2 pb-1 border-b border-border">{renderInline(b.text, `h2-${key}`)}</h2>
    case 'h3': return <h3 key={key} className="text-base font-semibold mt-4 mb-2">{renderInline(b.text, `h3-${key}`)}</h3>
    case 'p':  return <p  key={key} className="text-sm text-foreground leading-relaxed my-2">{renderInline(b.text, `p-${key}`)}</p>
    case 'quote': return (
      <blockquote key={key} className="border-l-2 border-acid/60 pl-3 my-3 text-sm text-muted-foreground italic">
        {renderInline(b.text, `q-${key}`)}
      </blockquote>
    )
    case 'ul': return (
      <ul key={key} className="list-disc pl-5 my-2 space-y-1 text-sm text-foreground">
        {b.items.map((it, i) => <li key={i}>{renderInline(it, `li-${key}-${i}`)}</li>)}
      </ul>
    )
    case 'ol': return (
      <ol key={key} className="list-decimal pl-5 my-2 space-y-1 text-sm text-foreground">
        {b.items.map((it, i) => <li key={i}>{renderInline(it, `li-${key}-${i}`)}</li>)}
      </ol>
    )
    case 'code': return (
      <pre key={key} className="my-3 p-3 rounded bg-muted overflow-x-auto text-xs font-mono">
        <code>{b.body}</code>
      </pre>
    )
    default: return null
  }
}
